import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import SearchableDropdown from '../../../../components/FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import useInterveneSx from '../../hooks/useInterveneSx';
import useCampaignFormOptions from '../../hooks/useCampaignFormOptions';
import { getCampaignProposalId } from '../../utils/leadFormList';

export default function LeadCampaignFormSelector({
  selectedCampaign,
  selectedForm,
  onApply,
  organisationId,
}) {
  const styles = useInterveneSx().contextDrawer;

  const {
    draft,
    formList,
    campaignList,
    campaignLoading,
    formLoading,
    handleSearchCampaign,
    loadMoreCampaigns,
    handleCampaignChange,
    handleFormChange,
  } = useCampaignFormOptions({ organisationId, selectedCampaign, selectedForm });

  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;
  const lastAppliedKeyRef = useRef('');

  useEffect(() => {
    if (!draft.campaign || !draft.webForm || formLoading) return;

    const campaignId = getCampaignProposalId(draft.campaign);
    const formId = draft.webForm?.leads_form_id;
    const applyKey = `${campaignId}:${formId}`;

    const campaignChanged =
      `${getCampaignProposalId(selectedCampaign)}` !== `${campaignId}`;
    const formChanged =
      `${selectedForm?.leads_form_id}` !== `${formId}`;

    if (!campaignChanged && !formChanged) {
      lastAppliedKeyRef.current = applyKey;
      return;
    }

    if (lastAppliedKeyRef.current === applyKey) return;

    lastAppliedKeyRef.current = applyKey;
    onApplyRef.current?.({ campaign: draft.campaign, webForm: draft.webForm });
  }, [
    draft.campaign,
    draft.webForm,
    formLoading,
    selectedCampaign,
    selectedForm,
  ]);

  return (
    <Box sx={styles.campaignSelectorCard}>
      <Typography sx={{...styles.overviewCardTitle , mb:1.5}}>Campaign &amp; Form</Typography>

      <Box sx={styles.campaignSelectorRow}>
        <Box sx={styles.campaignSelectorField}>
          <SearchableDropdown
            label={
              <Typography component="span" variant="body2">
                Campaign <Typography component="span" color="error.main">*</Typography>
              </Typography>
            }
            options={campaignList?.list || []}
            labelKey="name"
            valueKey="proposal_id"
            value={draft.campaign}
            onSearch={handleSearchCampaign}
            onChange={(value) => {
              lastAppliedKeyRef.current = '';
              handleCampaignChange(value);
            }}
            loading={campaignLoading}
            fetchMoreOptions={campaignList?.has_next ? loadMoreCampaigns : undefined}
            sx={{
              '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-root': {
                top: '0px',
              },
              '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-root.MuiInputLabel-shrink': {
                top: '-3.5px',
              },
            }}
          />
        </Box>

        <Box sx={styles.campaignSelectorField}>
          <SearchableDropdown
            label={
              <Typography component="span" variant="body2">
                Lead Form <Typography component="span" color="error.main">*</Typography>
              </Typography>
            }
            options={formList}
            labelKey="leads_form_name"
            valueKey="leads_form_id"
            value={draft.webForm}
            onChange={handleFormChange}
            loading={formLoading}
            disabled={!draft.campaign}
            sx={{
              '& .MuiFormLabel-root.MuiInputLabel-root': {
                top: '0px',
              },
              '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink': {
                top: '-3.5px',
              },
            }}
          />
        </Box>
      </Box>

      {!formLoading && draft.campaign && formList.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          No lead forms found for this campaign.
        </Typography>
      )}
    </Box>
  );
}
